// SF-reading, visibly-evolving city world. One instance per panel.
// Procedural baseline renders INSTANTLY; real GLB cars/trees pop in async from
// the shared cache (assetLoader). Uses Three.js Water + Sky shaders for the bay.
// worldState (0..1 params) drives a visible decade-by-decade evolution.

import * as THREE from 'three'
import { Water } from 'three/addons/objects/Water.js'
import { Sky } from 'three/addons/objects/Sky.js'
import { instance, URLS, preload } from './assetLoader.js'

const SPAN = 220
const HALF = SPAN / 2
const BLOCK = 26
const ROAD = 9
const EYE = 4.0

const PASTELS = ['#e8c1c8', '#cfe0ec', '#e9ddc7', '#cdd9c3', '#dcc9e0', '#f0d9b5', '#bcd4d6'] // Painted Ladies
const CAR_NEAR = ['sedan', 'suv', 'taxi', 'van', 'hatchback-sports', 'delivery', 'truck']
const TREES = ['MapleTree_1', 'MapleTree_3', 'BirchTree_2', 'BirchTree_4']

export class World {
  constructor(container, seed = 0) {
    this.container = container
    this.seed = seed
    // SHARED LAYOUT: geometry placement uses a FIXED seed so all four panels
    // build the identical San Francisco. Only worldState (per-panel agent
    // interpretation) diverges — four timelines of one city, not four cities.
    this.rng = mulberry(0xC0FFEE)
    this.focused = false
    this.throttle = false
    this.keys = {}
    this._frame = 0
    this.state = null

    const w = container.clientWidth || 640
    const h = container.clientHeight || 360
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    this.renderer.setSize(w, h, false)
    this.renderer.domElement.style.cssText = 'width:100%;height:100%;display:block'
    container.appendChild(this.renderer.domElement)

    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 6000)
    this.yaw = Math.PI * 0.85
    this.pos = new THREE.Vector3(-HALF * 0.5, 30, HALF * 0.7)

    this.hemi = new THREE.HemisphereLight(0xbfd4ff, 0x2a2620, 0.9); this.scene.add(this.hemi)
    this.sun = new THREE.DirectionalLight(0xfff2d8, 1.2); this.sun.position.set(60, 120, 40); this.scene.add(this.sun)
    this.fog = new THREE.FogExp2(0xbfc6d0, 0.0045); this.scene.fog = this.fog

    this.windowTex = makeWindowTexture()

    this._buildSkyAndOcean()
    this._buildTerrain()
    this._buildStreets()
    this._buildDowntown()
    this._buildResidential()
    this._buildBridge()
    this._buildAlcatraz()
    this._buildWharf()
    this._buildPrideFlags()
    this._buildTents()
    this._buildTraffic()
    this._buildPeople()
    this._buildWeather()

    this.clock = new THREE.Clock()
    this._onResize = () => this.resize()
    window.addEventListener('resize', this._onResize)
    this._loadAssets()
  }

  // ---- Sky + Water shader ocean -----------------------------------------------
  _buildSkyAndOcean() {
    this.sky = new Sky(); this.sky.scale.setScalar(5000); this.scene.add(this.sky)
    const u = this.sky.material.uniforms
    u.turbidity.value = 8; u.rayleigh.value = 2; u.mieCoefficient.value = 0.005; u.mieDirectionalG.value = 0.8
    this._sunVec = new THREE.Vector3()

    const waterGeo = new THREE.PlaneGeometry(SPAN * 3, SPAN * 3)
    this.water = new Water(waterGeo, {
      textureWidth: 256, textureHeight: 256,
      waterNormals: makeWaterNormals(),
      sunDirection: new THREE.Vector3(),
      sunColor: 0xffffff, waterColor: 0x223a4d, distortionScale: 3.0, fog: true
    })
    this.water.rotation.x = -Math.PI / 2
    this.water.position.y = 0.4
    this.scene.add(this.water)
  }

  _terrainH(x, z) {
    const west = Math.max(0, (-x + 30) / SPAN)
    // deterministic hills (no per-panel seed) — Twin Peaks SW dominant + rolling domes
    const hills = (Math.sin(x * 0.045) + Math.cos(z * 0.05)) * 6 + Math.sin(x * 0.02) * 5
    return Math.max(0, west * (hills + 8))
  }

  _buildTerrain() {
    const geo = new THREE.PlaneGeometry(SPAN * 1.5, SPAN * 1.5, 56, 56)
    geo.rotateX(-Math.PI / 2)
    const pos = geo.attributes.position
    for (let i = 0; i < pos.count; i++) pos.setY(i, this._terrainH(pos.getX(i), pos.getZ(i)) - 0.2)
    geo.computeVertexNormals()
    this.ground = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0x6b7464, roughness: 1 }))
    this.ground.position.y = 0.45
    this.scene.add(this.ground)
  }

  _buildStreets() {
    const g = new THREE.Group()
    const asphalt = new THREE.MeshStandardMaterial({ color: 0x2b2e34, roughness: 0.9 })
    const sidewalk = new THREE.MeshStandardMaterial({ color: 0x9a9ea6, roughness: 1 })
    const line = new THREE.MeshBasicMaterial({ color: 0xe8d98a })
    for (let i = -3; i <= 3; i++) {
      const o = i * BLOCK
      for (const dir of [0, 1]) {
        const road = new THREE.Mesh(new THREE.PlaneGeometry(dir ? ROAD : SPAN, dir ? SPAN : ROAD), asphalt)
        road.rotation.x = -Math.PI / 2; road.position.set(dir ? o : 0, 0.55, dir ? 0 : o); g.add(road)
        const sw = new THREE.Mesh(new THREE.PlaneGeometry(dir ? ROAD + 3 : SPAN, dir ? SPAN : ROAD + 3), sidewalk)
        sw.rotation.x = -Math.PI / 2; sw.position.set(dir ? o : 0, 0.5, dir ? 0 : o); g.add(sw)
        for (let s = -HALF; s < HALF; s += 8) {
          const dash = new THREE.Mesh(new THREE.PlaneGeometry(dir ? 0.5 : 3, dir ? 3 : 0.5), line)
          dash.rotation.x = -Math.PI / 2; dash.position.set(dir ? o : s + 1.5, 0.6, dir ? s + 1.5 : o); g.add(dash)
        }
      }
    }
    this.scene.add(g)
  }

  _buildDowntown() {
    this.towers = []
    this.signs = []
    const base = new THREE.BoxGeometry(1, 1, 1)
    const SUB = BLOCK / 2.4   // tighter sub-grid -> dense FiDi blocks
    // expanded downtown footprint, clustered towers per block
    for (let gx = 30; gx < HALF - 6; gx += SUB) {
      for (let gz = -HALF * 0.45; gz < HALF * 0.5; gz += SUB) {
        if (this.rng() < 0.12) continue                          // few gaps
        const cluster = 1 + (this.rng() < 0.5 ? 1 : 0)           // 1-2 towers grouped
        for (let c = 0; c < cluster; c++) {
          const wdt = 5 + this.rng() * 6, baseH = 18 + this.rng() * 78
          const mat = new THREE.MeshStandardMaterial({ color: 0x2f3b49, roughness: 0.35, metalness: 0.55, emissive: 0x0e151c, emissiveMap: this.windowTex.clone(), emissiveIntensity: 0.6 })
          mat.emissiveMap.needsUpdate = true; mat.emissiveMap.repeat.set(Math.max(2, Math.round(wdt / 2)), Math.max(3, Math.round(baseH / 4)))
          const m = new THREE.Mesh(base, mat)
          m.position.set(gx + (this.rng() - 0.5) * 6, 0, gz + (this.rng() - 0.5) * 6)
          m.userData = { baseH, r: this.rng() }; m.scale.set(wdt, baseH, wdt); m.position.y = baseH / 2 + 0.5
          this.scene.add(m); this.towers.push(m)
          // signage: glowing billboard panel on taller towers
          if (baseH > 55 && this.rng() < 0.5) {
            const sign = new THREE.Mesh(new THREE.PlaneGeometry(wdt * 0.9, 6), new THREE.MeshBasicMaterial({ color: ['#ff4d6d', '#7df9ff', '#ffd166', '#b388ff'][Math.floor(this.rng() * 4)] }))
            sign.position.set(m.position.x, baseH * 0.7, m.position.z + wdt / 2 + 0.1)
            this.scene.add(sign); this.signs.push(sign)
          }
        }
      }
    }
    const pyr = new THREE.Mesh(new THREE.ConeGeometry(8, 95, 4), new THREE.MeshStandardMaterial({ color: 0xd8d8d2, roughness: 0.6, metalness: 0.2 }))
    pyr.rotation.y = Math.PI / 4; pyr.position.set(50, 48, -50); this.scene.add(pyr); this.pyramid = pyr
    const sales = new THREE.Mesh(new THREE.CylinderGeometry(5, 8, 130, 18), new THREE.MeshStandardMaterial({ color: 0x6a7e8c, roughness: 0.25, metalness: 0.7, emissive: 0x223344, emissiveMap: this.windowTex.clone(), emissiveIntensity: 0.5 }))
    sales.position.set(78, 65.5, -22); this.scene.add(sales); this.salesforce = sales
  }

  _buildResidential() {
    this.houses = []
    const body = new THREE.BoxGeometry(1, 1, 1)
    const roofGeo = new THREE.CylinderGeometry(0.01, 1, 1, 4)
    for (let gx = -HALF + 12; gx < 30; gx += 6.5) {
      for (let gz = -HALF + 12; gz < HALF - 12; gz += 8) {
        if (nearRoad(gx) || nearRoad(gz) || this.rng() < 0.12) continue
        const h = 7 + this.rng() * 9, wdt = 5.5, dep = 8
        const col = PASTELS[Math.floor(this.rng() * PASTELS.length)]
        const grp = new THREE.Group()
        const wall = new THREE.Mesh(body, new THREE.MeshStandardMaterial({ color: col, roughness: 0.85 }))
        wall.scale.set(wdt, h, dep); wall.position.y = h / 2; grp.add(wall)
        const bay = new THREE.Mesh(body, new THREE.MeshStandardMaterial({ color: col, roughness: 0.85 }))
        bay.scale.set(wdt * 0.5, h * 0.7, 1.4); bay.position.set(0, h * 0.42, dep / 2 + 0.6); grp.add(bay)
        const roof = new THREE.Mesh(roofGeo, new THREE.MeshStandardMaterial({ color: 0x3a3530, roughness: 1 }))
        roof.rotation.y = Math.PI / 4; roof.scale.set(wdt * 0.8, 2.5, dep * 0.8); roof.position.y = h + 1.2; grp.add(roof)
        grp.position.set(gx, this._terrainH(gx, gz) + 0.45, gz)
        grp.userData = { baseH: h, r: this.rng() }
        this.scene.add(grp); this.houses.push(grp)
      }
    }
  }

  // The Golden Gate Bridge — International Orange towers, sagging suspension
  // cables with vertical suspenders, and a deck spanning the bay to the north.
  _buildBridge() {
    const g = new THREE.Group()
    const GG = new THREE.MeshStandardMaterial({ color: 0xc0362c, roughness: 0.55, metalness: 0.25 })
    const z = -108, deckY = 22, topY = 98, t1x = -26, t2x = 46
    // two towers (paired legs + three crossbeams)
    for (const tx of [t1x, t2x]) {
      for (const ox of [-5, 5]) { const leg = new THREE.Mesh(new THREE.BoxGeometry(4.5, topY, 4.5), GG); leg.position.set(tx + ox, topY / 2, z); g.add(leg) }
      for (const by of [deckY + 7, topY * 0.56, topY - 7]) { const beam = new THREE.Mesh(new THREE.BoxGeometry(17, 3.5, 5.5), GG); beam.position.set(tx, by, z); g.add(beam) }
    }
    // deck
    const deck = new THREE.Mesh(new THREE.BoxGeometry(190, 2.6, 10), new THREE.MeshStandardMaterial({ color: 0x9a3c2e, roughness: 0.85 }))
    deck.position.set((t1x + t2x) / 2, deckY, z); g.add(deck)
    // main suspension cables (catenary) on both edges + vertical suspenders
    for (const oz of [-4, 4]) {
      const pts = [
        new THREE.Vector3(t1x - 72, deckY, z + oz), new THREE.Vector3(t1x, topY - 5, z + oz),
        new THREE.Vector3((t1x + t2x) / 2, deckY + 9, z + oz), new THREE.Vector3(t2x, topY - 5, z + oz),
        new THREE.Vector3(t2x + 72, deckY, z + oz)
      ]
      const curve = new THREE.CatmullRomCurve3(pts)
      g.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 48, 0.8, 6), GG))
      for (let i = 1; i < 24; i++) { const p = curve.getPoint(i / 24); if (p.y > deckY + 1.5) { const s = new THREE.Mesh(new THREE.BoxGeometry(0.35, p.y - deckY, 0.35), GG); s.position.set(p.x, (p.y + deckY) / 2, p.z); g.add(s) } }
    }
    this.scene.add(g)
  }

  // Alcatraz island in the bay (rock + low cellblock + lighthouse)
  _buildAlcatraz() {
    const g = new THREE.Group()
    const rock = new THREE.Mesh(new THREE.SphereGeometry(11, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0x6a6258, roughness: 1 }))
    rock.scale.set(1.6, 0.5, 1); g.add(rock)
    const cell = new THREE.Mesh(new THREE.BoxGeometry(16, 6, 7), new THREE.MeshStandardMaterial({ color: 0xb8b2a6, roughness: 0.9 }))
    cell.position.y = 6; g.add(cell)
    const light = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 1, 9, 8), new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x554400 }))
    light.position.set(7, 9, 0); g.add(light)
    g.position.set(HALF * 0.2, 0.6, -HALF * 0.7); this.scene.add(g)
  }

  // Fisherman's Wharf — piers on pilings + sheds along the north waterfront
  _buildWharf() {
    const g = new THREE.Group()
    const wood = new THREE.MeshStandardMaterial({ color: 0x6e5743, roughness: 1 })
    const shed = new THREE.MeshStandardMaterial({ color: 0xb24a3c, roughness: 0.9 })
    for (let p = 0; p < 4; p++) {
      const px = -40 + p * 26
      const deck = new THREE.Mesh(new THREE.BoxGeometry(16, 1, 34), wood); deck.position.set(px, 2.2, -HALF + 22); g.add(deck)
      for (const ox of [-6, 6]) for (const oz of [-14, 14]) { const pile = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 5, 6), wood); pile.position.set(px + ox, 0.5, -HALF + 22 + oz); g.add(pile) }
      const house = new THREE.Mesh(new THREE.BoxGeometry(12, 6, 14), shed); house.position.set(px, 5.5, -HALF + 22); g.add(house)
    }
    this.scene.add(g)
  }

  // Pride flags (Castro nod) — small waving rainbow banners on poles
  _buildPrideFlags() {
    this.flags = []
    const rainbow = makeRainbowTexture()
    for (let i = 0; i < 6; i++) {
      const gx = -HALF + 20 + this.rng() * (HALF * 0.6)
      const gz = -HALF + 20 + this.rng() * (SPAN - 40)
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 9, 6), new THREE.MeshStandardMaterial({ color: 0x444 }))
      const y = this._terrainH(gx, gz)
      pole.position.set(gx, y + 4.5, gz)
      const flag = new THREE.Mesh(new THREE.PlaneGeometry(5, 3, 8, 1), new THREE.MeshBasicMaterial({ map: rainbow, side: THREE.DoubleSide }))
      flag.position.set(gx + 2.6, y + 7.5, gz); flag.userData = { base: flag.geometry.attributes.position.array.slice() }
      this.scene.add(pole); this.scene.add(flag); this.flags.push(flag)
    }
  }

  _buildTents() {
    this.tents = new THREE.Group()
    const geo = new THREE.ConeGeometry(2.4, 3.0, 4)
    for (let i = 0; i < 90; i++) {
      const c = ['#3a4a3a', '#5a4a36', '#46566a'][i % 3]
      const t = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: c, roughness: 1 }))
      const gx = -HALF + 18 + this.rng() * (HALF * 0.9), gz = -HALF + 14 + this.rng() * (SPAN - 28)
      t.position.set(gx, this._terrainH(gx, gz) + 1.3, gz); t.rotation.y = this.rng() * Math.PI; t.visible = false
      this.tents.add(t)
    }
    this.scene.add(this.tents)
  }

  // weather: rain / snow particle fields that follow the camera
  _buildWeather() {
    const mk = (count, color, size, spreadY) => {
      const g = new THREE.BufferGeometry(); const p = new Float32Array(count * 3)
      for (let i = 0; i < count; i++) { p[i*3] = (Math.random()-0.5)*300; p[i*3+1] = Math.random()*spreadY; p[i*3+2] = (Math.random()-0.5)*300 }
      g.setAttribute('position', new THREE.BufferAttribute(p, 3))
      const m = new THREE.PointsMaterial({ color, size, transparent: true, opacity: 0.75, depthWrite: false })
      const pts = new THREE.Points(g, m); pts.visible = false; pts.frustumCulled = false; this.scene.add(pts); return pts
    }
    this.rain = mk(1800, 0xa9ccff, 0.7, 140); this.rain.userData = { speed: 170, drift: 0 }
    this.snow = mk(1300, 0xffffff, 1.6, 140); this.snow.userData = { speed: 16, drift: 1 }
  }

  // Always-on procedural traffic (works with no GLB assets) — colored cars that
  // drive the street grid. GLB car models layer on top when available.
  _buildTraffic() {
    if (!this.cars) this.cars = []
    const bodyGeo = new THREE.BoxGeometry(2, 0.9, 4.2), cabGeo = new THREE.BoxGeometry(1.8, 0.7, 2.2)
    const cols = [0xffffff, 0x1a1a1a, 0xcc3333, 0x3366cc, 0xffcc00, 0x888888, 0x22aa55, 0xaa44aa]
    for (let i = 0; i < 28; i++) {
      const grp = new THREE.Group()
      const body = new THREE.Mesh(bodyGeo, new THREE.MeshStandardMaterial({ color: cols[i % cols.length], metalness: 0.5, roughness: 0.4 }))
      body.position.y = 0.7; grp.add(body)
      const cab = new THREE.Mesh(cabGeo, new THREE.MeshStandardMaterial({ color: 0x0b0b10, metalness: 0.3, roughness: 0.2 }))
      cab.position.set(0, 1.3, -0.3); grp.add(cab)
      const axis = i % 2 === 0, o = (((i % 7) - 3)) * BLOCK
      grp.userData = { axis, o, t: (this.rng() - 0.5) * SPAN, speed: 9 + this.rng() * 16, dir: this.rng() < 0.5 ? 1 : -1 }
      this.scene.add(grp); this.cars.push(grp)
    }
  }

  // Pedestrians walking the sidewalks.
  _buildPeople() {
    this.people = []
    const geo = new THREE.CapsuleGeometry(0.32, 0.95, 4, 6)
    for (let i = 0; i < 64; i++) {
      const p = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: new THREE.Color().setHSL(this.rng(), 0.45, 0.5), roughness: 1 }))
      const onX = this.rng() < 0.5
      const lane = (Math.floor(this.rng() * 7) - 3) * BLOCK + (this.rng() < 0.5 ? ROAD / 2 + 1.5 : -ROAD / 2 - 1.5)
      const t = (this.rng() - 0.5) * SPAN
      const gx = onX ? lane : t, gz = onX ? t : lane
      p.position.set(gx, this._terrainH(gx, gz) + 0.95, gz)
      p.userData = { onX, lane, t, speed: 1.2 + this.rng() * 2.2, dir: this.rng() < 0.5 ? 1 : -1 }
      this.scene.add(p); this.people.push(p)
    }
  }

  async _loadAssets() {
    this.cars = this.cars || []; this.trees = this.trees || []
    try {
      const names = CAR_NEAR.slice(0, 5)
      await preload(names.map(URLS.car))
      for (let i = 0; i < 12; i++) {
        const { obj } = await instance(URLS.car(names[i % names.length]))
        fit(obj, 4.4)
        const axis = i % 2 === 0, o = (((i % 7) - 3)) * BLOCK
        obj.userData = { axis, o, t: (this.rng() - 0.5) * SPAN, speed: 7 + this.rng() * 12, dir: this.rng() < 0.5 ? 1 : -1 }
        this.scene.add(obj); this.cars.push(obj)
      }
    } catch (e) { console.warn('[World] cars skipped', e?.message) }
    try {
      const tn = TREES.slice(0, 4)
      await preload(tn.map(URLS.nature))
      for (let i = 0; i < 14; i++) {
        const { obj } = await instance(URLS.nature(tn[i % tn.length]))
        fit(obj, 6 + this.rng() * 4)
        const gx = (this.rng() < 0.5 ? -1 : 1) * (HALF * 0.3 + this.rng() * HALF * 0.5)
        const gz = (this.rng() < 0.5 ? -1 : 1) * (HALF * 0.3 + this.rng() * HALF * 0.5)
        obj.position.set(gx, this._terrainH(gx, gz) + 0.45, gz); obj.rotation.y = this.rng() * Math.PI
        this.scene.add(obj); this.trees.push(obj)
      }
    } catch (e) { console.warn('[World] trees skipped', e?.message) }
    if (this.state) this.setState(this.state)
  }

  setState(ws) {
    if (!ws) return
    this.state = ws
    // dramatic, decade-scaled height range; render() eases toward these targets
    const hMul = 0.45 + ws.buildingHeight * 1.7, shine = ws.shininess
    for (const m of this.towers) {
      const u = m.userData
      if (u.curH == null) u.curH = u.baseH
      const broken = ws.destruction > u.r
      u.targetH = broken ? Math.max(2, u.baseH * (1 - ws.destruction) * 0.4) : u.baseH * hMul
      u.targetTilt = broken ? (u.r - 0.5) * 0.9 : 0
      m.material.metalness = 0.3 + shine * 0.6; m.material.roughness = 0.55 - shine * 0.4
      m.material.color.lerpColors(new THREE.Color(0x33414f), new THREE.Color(ws.palette), shine * 0.55)
    }
    if (this.salesforce) this.salesforce.userData.targetScaleY = hMul
    if (this.pyramid) this.pyramid.visible = ws.destruction < 0.7
    for (const grp of this.houses) {
      const u = grp.userData
      if (u.curScaleY == null) u.curScaleY = 1
      const broken = ws.destruction > u.r + 0.15
      u.targetScaleY = broken ? 0.35 : 1
      u.targetTilt = broken ? (u.r - 0.5) * 0.6 : 0
    }
    this._tentTarget = Math.floor(ws.tents * this.tents.children.length)
    if (this.trees) for (const t of this.trees) t.visible = ws.greenery > 0.12

    const day = ws.timeOfDay
    this.fog.density = 0.0025 + ws.fog * 0.02
    this.hemi.intensity = 0.35 + day * 0.8
    this.sun.intensity = 0.25 + day * 1.3
    this.sun.color.set(day < 0.3 ? 0x3a5a9a : day > 0.78 ? 0xffcf94 : 0xfff2d8)
    // drive Sky + Water sun by day param
    if (this.sky) {
      const elev = THREE.MathUtils.lerp(2, 60, day), az = 180
      const phi = THREE.MathUtils.degToRad(90 - elev), theta = THREE.MathUtils.degToRad(az)
      this._sunVec.setFromSphericalCoords(1, phi, theta)
      this.sky.material.uniforms.sunPosition.value.copy(this._sunVec)
      this.sun.position.copy(this._sunVec).multiplyScalar(300)
      if (this.water) { this.water.material.uniforms.sunDirection.value.copy(this._sunVec).normalize(); this.water.material.uniforms.waterColor.value.setHSL(0.57, 0.5, 0.1 + day * 0.12) }
      this.fog.color.setHSL(0.6, 0.4, 0.1 + day * 0.25)
    }
    const glow = 0.25 + (1 - day) * 1.1
    for (const m of this.towers) m.material.emissiveIntensity = glow
    if (this.salesforce) this.salesforce.material.emissiveIntensity = glow

    this._flying = ws.flyingCars; this._accent = new THREE.Color(ws.palette)

    // weather variants
    const w = ws.weather || 'clear'
    if (this.rain) this.rain.visible = (w === 'rain')
    if (this.snow) this.snow.visible = (w === 'snow')
    if (w === 'smog' || w === 'dust') this.fog.density = Math.max(this.fog.density, 0.03)
  }

  focus(on) { this.focused = on; this.container.style.cursor = on ? 'none' : 'pointer' }
  setThrottle(t) { this.throttle = t }
  onKey(e, down) { const k = e.key.toLowerCase(); if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) { this.keys[k] = down; e.preventDefault() } }
  onMouseMove(dx) { if (this.focused) this.yaw -= dx * 0.0025 }

  _move(dt) {
    const f = (this.keys['w'] || this.keys['arrowup'] ? 1 : 0) - (this.keys['s'] || this.keys['arrowdown'] ? 1 : 0)
    const s = (this.keys['d'] || this.keys['arrowright'] ? 1 : 0) - (this.keys['a'] || this.keys['arrowleft'] ? 1 : 0)
    const spd = 26 * dt
    this.pos.x += (Math.sin(this.yaw) * f + Math.cos(this.yaw) * s) * spd
    this.pos.z += (Math.cos(this.yaw) * f - Math.sin(this.yaw) * s) * spd
    const lim = HALF * 0.95
    this.pos.x = Math.max(-lim, Math.min(lim, this.pos.x)); this.pos.z = Math.max(-lim, Math.min(lim, this.pos.z))
    this.pos.y = this._terrainH(this.pos.x, this.pos.z) + EYE
  }

  render() {
    this._frame++
    if (this.throttle && this._frame % 2) return
    const dt = Math.min(this.clock.getDelta(), 0.05), t = this.clock.elapsedTime
    if (this.water) this.water.material.uniforms.time.value += dt * 0.6

    if (this.focused) {
      this._move(dt); this.camera.position.copy(this.pos)
      this.camera.lookAt(this.pos.x + Math.sin(this.yaw) * 6, this.pos.y - 0.5, this.pos.z + Math.cos(this.yaw) * 6)
    } else {
      // Cinematic orbit close enough to read street detail — tents, traffic,
      // and buildings rising/crumbling are all clearly visible as the city evolves.
      const a = t * 0.06
      this.pos.set(Math.sin(a) * HALF * 0.55, 34, Math.cos(a) * HALF * 0.55)
      this.camera.position.copy(this.pos)
      this.camera.lookAt(0, 9, 0)
    }

    const fly = this._flying || 0
    if (this.cars) for (const car of this.cars) {
      const u = car.userData; u.t += u.speed * dt * u.dir
      if (u.t > HALF) u.t = -HALF; if (u.t < -HALF) u.t = HALF
      const gx = u.axis ? u.o : u.t, gz = u.axis ? u.t : u.o
      const lift = fly * (5 + (Math.abs(u.o) % 9))
      car.position.set(gx, this._terrainH(gx, gz) + 0.6 + lift, gz)
      car.rotation.y = (u.axis ? 0 : Math.PI / 2) + (u.dir < 0 ? Math.PI : 0)
    }
    // waving flags
    if (this.flags) for (const fl of this.flags) {
      const p = fl.geometry.attributes.position, b = fl.userData.base
      for (let i = 0; i < p.count; i++) { const x = b[i * 3]; p.setZ(i, Math.sin(t * 3 + x * 1.5) * 0.4 * (x + 2.5) / 5) }
      p.needsUpdate = true
    }
    // ARCHITECTURAL EVOLUTION: ease every structure toward its target each frame
    // so the city visibly grows, modernizes, and crumbles over time.
    const k = Math.min(1, dt * 1.6)
    if (this.towers) for (const m of this.towers) {
      const u = m.userData
      if (u.targetH == null) continue
      u.curH += (u.targetH - u.curH) * k
      u.curTilt = (u.curTilt || 0) + ((u.targetTilt || 0) - (u.curTilt || 0)) * k
      m.scale.y = u.curH; m.position.y = u.curH / 2 + 0.5; m.rotation.z = u.curTilt
    }
    if (this.salesforce && this.salesforce.userData.targetScaleY != null) {
      const u = this.salesforce.userData; u.curScaleY = (u.curScaleY || 1) + (u.targetScaleY - (u.curScaleY || 1)) * k; this.salesforce.scale.y = u.curScaleY
    }
    if (this.houses) for (const grp of this.houses) {
      const u = grp.userData
      if (u.targetScaleY == null) continue
      u.curScaleY += (u.targetScaleY - u.curScaleY) * k
      u.curTilt = (u.curTilt || 0) + ((u.targetTilt || 0) - (u.curTilt || 0)) * k
      grp.scale.y = u.curScaleY; grp.rotation.z = u.curTilt
    }
    if (this.tents && this._tentTarget != null) {
      this.tents.children.forEach((tt, i) => (tt.visible = i < this._tentTarget))
    }

    // pedestrians walk the sidewalks
    if (this.people) for (const p of this.people) {
      const u = p.userData; u.t += u.speed * dt * u.dir
      if (u.t > SPAN / 2) u.t = -SPAN / 2; if (u.t < -SPAN / 2) u.t = SPAN / 2
      const gx = u.onX ? u.lane : u.t, gz = u.onX ? u.t : u.lane
      p.position.set(gx, this._terrainH(gx, gz) + 0.95, gz)
    }

    // weather animation — particles fall and wrap, field follows the camera
    for (const pts of [this.rain, this.snow]) {
      if (!pts || !pts.visible) continue
      const a = pts.geometry.attributes.position, sp = pts.userData.speed * dt
      for (let i = 0; i < a.count; i++) {
        let y = a.getY(i) - sp
        if (y < 0) y = 140
        a.setY(i, y)
        if (pts.userData.drift) a.setX(i, a.getX(i) + Math.sin((y + i) * 0.1) * 0.25)
      }
      a.needsUpdate = true
      pts.position.set(this.pos.x, 0, this.pos.z)
    }

    this.renderer.render(this.scene, this.camera)
  }

  resize() {
    const w = this.container.clientWidth, h = this.container.clientHeight
    if (!w || !h) return
    this.renderer.setSize(w, h, false); this.camera.aspect = w / h; this.camera.updateProjectionMatrix()
  }

  dispose() {
    window.removeEventListener('resize', this._onResize)
    this.scene.traverse(o => { if (o.geometry) o.geometry.dispose?.(); const m = o.material; if (m) (Array.isArray(m) ? m : [m]).forEach(x => x.dispose?.()) })
    this.renderer.dispose(); this.renderer.domElement.parentNode?.removeChild(this.renderer.domElement)
  }
}

// ---- helpers -----------------------------------------------------------------
function nearRoad(v) { for (let i = -3; i <= 3; i++) if (Math.abs(v - i * BLOCK) < ROAD) return true; return false }
function fit(obj, targetH) {
  const box = new THREE.Box3().setFromObject(obj), size = new THREE.Vector3(); box.getSize(size)
  obj.scale.setScalar(targetH / (size.y || 1))
  const b2 = new THREE.Box3().setFromObject(obj); obj.position.y -= b2.min.y
}
function makeWindowTexture() {
  const c = document.createElement('canvas'); c.width = 64; c.height = 64; const x = c.getContext('2d')
  x.fillStyle = '#05080c'; x.fillRect(0, 0, 64, 64)
  for (let yy = 4; yy < 64; yy += 8) for (let xx = 4; xx < 64; xx += 8) { x.fillStyle = Math.random() < 0.6 ? '#ffe6a8' : '#22303c'; x.fillRect(xx, yy, 5, 5) }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t
}
function makeRainbowTexture() {
  const c = document.createElement('canvas'); c.width = 6; c.height = 6; const x = c.getContext('2d')
  const cols = ['#e40303', '#ff8c00', '#ffed00', '#008026', '#004dff', '#750787']
  cols.forEach((col, i) => { x.fillStyle = col; x.fillRect(0, i, 6, 1) })
  const t = new THREE.CanvasTexture(c); t.magFilter = THREE.NearestFilter; return t
}
function makeWaterNormals() {
  const s = 128, c = document.createElement('canvas'); c.width = c.height = s; const ctx = c.getContext('2d')
  const img = ctx.createImageData(s, s)
  for (let y = 0; y < s; y++) for (let xx = 0; xx < s; xx++) {
    const i = (y * s + xx) * 4
    const nx = Math.sin(xx * 0.2) * Math.cos(y * 0.13), ny = Math.cos(xx * 0.16) * Math.sin(y * 0.22)
    img.data[i] = 128 + nx * 60; img.data[i + 1] = 128 + ny * 60; img.data[i + 2] = 255; img.data[i + 3] = 255
  }
  ctx.putImageData(img, 0, 0)
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; return t
}
function mulberry(a) {
  return function () { a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296 }
}
